import {
  Box,
  IconButton,
  Stack,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { FC, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaXmark } from "react-icons/fa6";
import { TfiHeadphoneAlt } from "react-icons/tfi";
import { TiThMenu } from "react-icons/ti";
import { SalifySocialIcon } from "src/lib/components/ui/icon/social-icon";
import { SalifyLinkV2 } from "src/lib/components/ui/link/link";
import { HeaderNavInterface, SocialGroupInterface } from "src/lib/utils/interfaces";
import { effectiveNovaPressSubmenu } from "../effective-submenu";

interface NovaPressResponsiveHeaderNavProps {
  nav: Array<HeaderNavInterface>;
  socialGroup: Array<SocialGroupInterface> | undefined;
  isActive: (item: any, sectionId: string | undefined) => boolean;
  onClick: (item: any, sectionId: string | undefined) => void;
  hotline: string | undefined;
}

export const NovaPressResponsiveHeaderNav: FC<NovaPressResponsiveHeaderNavProps> = (props) => {
  const theme = useTheme();
  const [openMenu, setOpenMenu] = useState(false);
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  const closeMenu = useCallback(() => setOpenMenu(false), []);

  const toggleMenu = useCallback(() => {
    setOpenMenu((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openMenu, closeMenu]);

  useEffect(() => {
    if (!openMenu) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [openMenu]);

  const handleItemClick = (index: number) => {
    setExpandedItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const menuLayer =
    openMenu &&
    typeof document !== "undefined" &&
    createPortal(
      <>
        <Box
          role="presentation"
          onClick={closeMenu}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: (t) => t.zIndex.modal,
            bgcolor: alpha(theme.palette.common.black, 0.52),
          }}
        />
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: "min(72vw, 360px)",
            maxWidth: "90vw",
            zIndex: (t) => t.zIndex.modal + 1,
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
            boxShadow: theme.shadows[16],
            overflowY: "auto",
          }}
        >
          <IconButton
            type="button"
            aria-label="close"
            sx={{ alignSelf: "flex-end", mt: 0.5, mr: 0.5 }}
            onClick={closeMenu}
          >
            <FaXmark size={30} />
          </IconButton>

          <List component="nav" sx={{ width: "100%", py: 0 }}>
            {props.nav.map((item, index) => {
              const sectionId = item.target?.replace("#", "");
              const isItemActive = props.isActive(item, sectionId);
              const hasSubmenu = effectiveNovaPressSubmenu(item).length > 0;
              const isExpanded = expandedItems.includes(index);

              return (
                <Box key={index}>
                  <ListItemButton
                    component={item.action === "link" ? "a" : "div"}
                    href={item.action === "link" ? item.target : undefined}
                    onClick={(e: React.MouseEvent) => {
                      if (hasSubmenu) {
                        e.preventDefault();
                        handleItemClick(index);
                      } else {
                        if (item.action === "scroll") {
                          e.preventDefault();
                          props.onClick(item, sectionId);
                        }
                        closeMenu();
                      }
                    }}
                    sx={{
                      paddingX: 4,
                      paddingY: 2,
                      fontWeight: isItemActive ? 800 : 700,
                      backgroundColor: isItemActive ? "primary.main" : "transparent",
                      color: isItemActive ? "white" : "inherit",
                      "&:hover": {
                        backgroundColor: "primary.light",
                      },
                    }}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>

                  {hasSubmenu && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {effectiveNovaPressSubmenu(item).map((subItem, subIndex) => {
                          const subSectionId = subItem.target?.replace("#", "");
                          const isSubItemActive = props.isActive(subItem, subSectionId);

                          return (
                            <ListItemButton
                              key={subIndex}
                              component={subItem.action === "link" ? "a" : "div"}
                              href={subItem.action === "link" ? subItem.target : undefined}
                              onClick={(e: React.MouseEvent) => {
                                if (subItem.action === "scroll") {
                                  e.preventDefault();
                                  props.onClick(subItem, subSectionId);
                                }
                                closeMenu();
                              }}
                              sx={{
                                pl: 8,
                                py: 1.5,
                                fontWeight: isSubItemActive ? 800 : 700,
                                backgroundColor: isSubItemActive ? "primary.main" : "transparent",
                                color: isSubItemActive ? "white" : "inherit",
                                "&:hover": {
                                  backgroundColor: "primary.light",
                                },
                              }}
                            >
                              <ListItemText primary={subItem.label} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>

          {!!props.socialGroup?.length && (
            <Stack direction="row" spacing={2} py={2} alignSelf="center">
              {props.socialGroup.map((item, index) => {
                if (!item.url) return null;

                return (
                  <SalifyLinkV2 href={item.url} key={index}>
                    <SalifySocialIcon type={item.type} />
                  </SalifyLinkV2>
                );
              })}
            </Stack>
          )}
        </Box>
      </>,
      document.body,
    );

  return (
    <>
      <Box display="flex" gap={1} alignItems="center" sx={{ touchAction: "manipulation" }}>
        {props.hotline && (
          <IconButton
            type="button"
            color="primary"
            aria-label="call"
            onClick={() => {
              window.location.href = `tel:${props.hotline}`;
            }}
            sx={{
              padding: { xs: 0.75, sm: 1 },
            }}
          >
            <TfiHeadphoneAlt size={20} />
          </IconButton>
        )}
        <IconButton
          type="button"
          aria-label="menu"
          aria-expanded={openMenu}
          onClick={toggleMenu}
          sx={{
            padding: { xs: 0.75, sm: 1 },
            minWidth: 44,
            minHeight: 44,
            color: "primary.main",
            "& svg": { pointerEvents: "none" },
          }}
        >
          <TiThMenu size={24} />
        </IconButton>
      </Box>

      {menuLayer}
    </>
  );
};

NovaPressResponsiveHeaderNav.displayName = "NovaPressResponsiveHeaderNav";
